import React, { useCallback, useState } from 'react';
import { useT } from '../i18n';
import { useSession } from '../context/Session';
import { Empty, Icon, Loading } from '../components/ui';
import { adminXpLeaderboard } from '../lib/cloud';
import { XP_FOUNDER_TITLE, XP_TITLES } from '../lib/xp';

// Admin panel: users with the most XP (fitviz_cloud xp.admin_leaderboard).

const DEFAULT_K = 10;
const MAX_K = 500;

function titleFor(level) {
  let id = XP_TITLES[0].id;
  for (const title of XP_TITLES) if (level >= title.level) id = title.id;
  return id;
}

export default function AdminLeaderboard() {
  const t = useT();
  const { refreshAdmin } = useSession();
  const [k, setK] = useState(String(DEFAULT_K));
  // Loads only on Refresh (also the first time): undefined = not loaded yet, null = loading.
  const [users, setUsers] = useState(undefined);
  const [error, setError] = useState('');

  const load = useCallback(async (limit) => {
    setUsers(null);
    setError('');
    try {
      let res = await adminXpLeaderboard(limit);
      if (res.error === 'forbidden') {
        // Claim may have been granted after this session's token was minted.
        if (await refreshAdmin(true)) res = await adminXpLeaderboard(limit);
      }
      if (res.users) setUsers(res.users);
      else {
        setUsers([]);
        setError(res.error === 'forbidden' ? t('anErrorOccurred') : String(res.error || ''));
      }
    } catch (e) {
      setUsers([]);
      setError(t('anErrorOccurred'));
    }
  }, [refreshAdmin, t]);

  const submit = (e) => {
    e.preventDefault();
    const n = Math.max(1, Math.min(MAX_K, Number(k) || DEFAULT_K));
    setK(String(n));
    load(n);
  };

  return (
    <div className="gm-stack">
      <form className="gm-row" style={{ justifyContent: 'space-between', gap: 8 }} onSubmit={submit}>
        <div className="gm-row" style={{ gap: 8 }}>
          <label htmlFor="adm-top-k" className="gm-small gm-muted">{t('adminLbTop')}</label>
          <input
            id="adm-top-k"
            className="gm-input"
            style={{ width: 90 }}
            inputMode="numeric"
            value={k}
            onChange={(e) => setK(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </div>
        <button type="submit" className="gm-btn gm-btn--soft gm-btn--sm">
          <Icon name="refresh" size={18} /> {t('adminRefresh')}
        </button>
      </form>
      <section className="gm-card gm-card--pad-lg">
        {users === undefined && <Empty icon="refresh">{t('adminPressRefresh')}</Empty>}
        {users === null && <Loading />}
        {error && <p className="gm-error-text gm-small">{error}</p>}
        {users && users.length === 0 && !error && <Empty icon="leaderboard">{t('adminLbEmpty')}</Empty>}
        {users && users.length > 0 && (
          <div className="gm-admin-table-wrap">
            <table className="gm-admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('email')}</th>
                  <th>{t('adminLbLevel')}</th>
                  <th>XP</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.uid}>
                    <td className="gm-strong">{i + 1}</td>
                    <td>
                      <div className="gm-strong">{u.name || '—'}</div>
                      <div className="gm-small gm-muted">{u.email || u.uid}</div>
                    </td>
                    <td>
                      {u.level}
                      <div className="gm-small gm-muted">
                        {t.xpTitle(titleFor(u.level))}
                        {u.founder ? ` · ${t.xpTitle(XP_FOUNDER_TITLE)}` : ''}
                      </div>
                    </td>
                    <td className="gm-strong">{u.xp.toLocaleString(t.locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
